import type { ReactNode } from "react";
import { create } from "zustand";

import { Root as AdaptiveModalRoot } from "./adaptive-modal";
import type { GlobalModalProps, ModalEntry, ModalRenderProps } from "./types";

/**
 * Exit-animation grace period (ms). A closing entry stays mounted this long —
 * `open` flipped to `false` — so its surface can play its close animation
 * before the entry is dropped from the stack. DS-046 fixes this at 300ms; it
 * covers Dialog (web 200ms / native 150ms) and the native BottomSheet (300ms).
 */
const EXIT_ANIMATION_MS = 300;

interface ModalStoreState {
  entries: ModalEntry[];
}

/**
 * The single global modal stack. A zustand store is used (rather than the
 * sonner-style primitive behind the Toast molecule) because entries are
 * arbitrary render functions and the stack needs per-entry overlay control —
 * neither of which a toast primitive exposes. There is exactly one store in the
 * system — do not create a second one (see DS-046: no `DialogHost` /
 * `BottomSheetHost`).
 */
const useModalStore = create<ModalStoreState>()(() => ({ entries: [] }));

// Random per module-load: Fast Refresh resets `idCounter`, so a bare counter
// could re-issue an id still held by a live entry. The prefix keeps ids unique
// across reloads.
const idPrefix = Math.random().toString(36).slice(2, 10);
let idCounter = 0;
const nextModalId = (): string => `adaptive-modal-${idPrefix}-${++idCounter}`;

/**
 * Push a custom modal render function onto the stack.
 *
 * @returns the new entry's id — pass it to `closeModal` to target this entry.
 */
export const openModal = (render: (props: ModalRenderProps) => ReactNode): string => {
  const id = nextModalId();
  useModalStore.setState((state) => ({
    entries: [...state.entries, { id, render, open: true }],
  }));
  return id;
};

/**
 * Flip `open` to `false` for the given entries so their surfaces animate out,
 * then drop exactly those entries once the animation has had time to finish.
 * Removal is id-scoped, so a modal opened during the grace period survives.
 */
const closeEntries = (ids: string[]): void => {
  if (ids.length === 0) return;
  const closing = new Set(ids);

  useModalStore.setState((state) => ({
    entries: state.entries.map((entry) =>
      closing.has(entry.id) ? { ...entry, open: false } : entry,
    ),
  }));
  setTimeout(() => {
    useModalStore.setState((state) => ({
      entries: state.entries.filter((entry) => !closing.has(entry.id)),
    }));
  }, EXIT_ANIMATION_MS);
};

/**
 * Close the top-most modal, or a specific entry by id. The entry animates out
 * before it is removed. A no-op if the target is missing or already closing.
 */
export const closeModal = (id?: string): void => {
  const { entries } = useModalStore.getState();
  const target = id ? entries.find((entry) => entry.id === id) : entries[entries.length - 1];
  if (!target || !target.open) return;
  closeEntries([target.id]);
};

/** Close every open modal, animating them all out in parallel. */
export const closeAllModals = (): void => {
  const openIds = useModalStore
    .getState()
    .entries.filter((entry) => entry.open)
    .map((entry) => entry.id);
  closeEntries(openIds);
};

/**
 * Convenience over `openModal`: push an `AdaptiveModal` with standard compound
 * props. The managed `open` flag and the stack-aware overlay are wired for you.
 *
 * Two behaviors to note:
 * - A custom `overlay` is honored only while the entry is bottom-most; stacked
 *   entries render no overlay (the stack shows a single scrim).
 * - `onOpenChange` fires for surface-driven closes (overlay tap, Escape,
 *   swipe) but not for an imperative `closeModal()` — keep the returned id to
 *   track programmatic closes.
 *
 * @returns the new entry's id.
 */
export const openModalSheet = (props: GlobalModalProps): string => {
  const { children, onOpenChange, overlay, ...rest } = props;
  return openModal(({ open, close, showOverlay }) => (
    <AdaptiveModalRoot
      {...rest}
      open={open}
      onOpenChange={(next) => {
        onOpenChange?.(next);
        if (!next) close();
      }}
      overlay={showOverlay ? overlay : null}
    >
      {children}
    </AdaptiveModalRoot>
  ));
};

/** Subscribe a component to the current modal stack. */
export const useModalEntries = (): ModalEntry[] => useModalStore((state) => state.entries);
